import type { FinancingSummary } from '../../services/financeCalculations';
import { formatCurrency } from '../../utils/pricing';
import { formatRate } from '../../services/financeCalculations';

interface LiquiditaSummaryProps {
  summary: FinancingSummary;
  totalCosts: number;
  initialCash?: number;
}

export function LiquiditaSummary({
  summary,
  totalCosts,
  initialCash = 0,
}: LiquiditaSummaryProps) {
  const liquidita = initialCash + summary.totalImporto - totalCosts;
  const isPositive = liquidita >= 0;
  const coveragePercentage = totalCosts > 0
    ? Math.min(100, (summary.totalImporto / totalCosts) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Main Summary Card */}
      <div className={`rounded-xl p-6 ${
        isPositive ? 'bg-success-50 border border-success-200' : 'bg-danger-50 border border-danger-200'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-sm font-medium ${isPositive ? 'text-success-800' : 'text-danger-800'}`}>
              Saldo Liquidita
            </h3>
            <p className={`text-3xl font-bold mt-1 currency ${isPositive ? 'text-success-700' : 'text-danger-700'}`}>
              {formatCurrency(liquidita)}
            </p>
          </div>
          <div className={`p-3 rounded-full ${isPositive ? 'bg-success-100' : 'bg-danger-100'}`}>
            {isPositive ? (
              <svg className="w-8 h-8 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
          </div>
        </div>

        {!isPositive && (
          <p className="mt-3 text-sm text-danger-700">
            Attenzione: i finanziamenti non coprono completamente i costi previsti.
            Servono {formatCurrency(Math.abs(liquidita))} aggiuntivi.
          </p>
        )}
      </div>

      {/* Coverage Bar */}
      <div className="card">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Copertura Costi</span>
          <span className="text-sm font-bold text-gray-900">{coveragePercentage.toFixed(1)}%</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              coveragePercentage >= 100 ? 'bg-success-500' : coveragePercentage >= 80 ? 'bg-warning-500' : 'bg-danger-500'
            }`}
            style={{ width: `${Math.min(100, coveragePercentage)}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>Finanziato: {formatCurrency(summary.totalImporto)}</span>
          <span>Costi totali: {formatCurrency(totalCosts)}</span>
        </div>
      </div>

      {/* Breakdown */}
      <div className={`grid grid-cols-1 ${summary.fondiPropri.count > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
        {/* Mutui */}
        <div className="card bg-primary-50 border border-primary-200">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <h4 className="font-medium text-primary-800">Mutui</h4>
          </div>
          {summary.mutui.count > 0 ? (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-primary-700">Importo totale</span>
                <span className="font-semibold text-primary-800 currency">
                  {formatCurrency(summary.mutui.importo)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-primary-700">Rata mensile</span>
                <span className="font-semibold text-primary-800 currency">
                  {formatCurrency(summary.mutui.rata)}
                </span>
              </div>
              <p className="text-xs text-primary-600">{summary.mutui.count} mutuo/i attivi</p>
            </div>
          ) : (
            <p className="text-sm text-primary-600">Nessun mutuo configurato</p>
          )}
        </div>

        {/* Prestiti */}
        <div className="card bg-success-50 border border-success-200">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="font-medium text-success-800">Prestiti</h4>
          </div>
          {summary.prestiti.count > 0 ? (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-success-700">Importo totale</span>
                <span className="font-semibold text-success-800 currency">
                  {formatCurrency(summary.prestiti.importo)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-success-700">Rata mensile</span>
                <span className="font-semibold text-success-800 currency">
                  {formatCurrency(summary.prestiti.rata)}
                </span>
              </div>
              <p className="text-xs text-success-600">{summary.prestiti.count} prestito/i attivi</p>
            </div>
          ) : (
            <p className="text-sm text-success-600">Nessun prestito configurato</p>
          )}
        </div>

        {/* Fondi Propri */}
        {summary.fondiPropri.count > 0 && (
          <div className="card bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h4 className="font-medium text-amber-800">Fondi Propri</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-amber-700">Importo totale</span>
                <span className="font-semibold text-amber-800 currency">
                  {formatCurrency(summary.fondiPropri.importo)}
                </span>
              </div>
              <p className="text-xs text-amber-600">
                {summary.fondiPropri.count} voce/i
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Total Monthly Payment */}
      {summary.totalRataMensile > 0 && (
        <div className="card bg-gray-900 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-300">Rata Mensile Totale</h4>
              <p className="text-2xl font-bold mt-1 currency">
                {formatCurrency(summary.totalRataMensile)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Tasso medio ponderato</p>
              <p className="text-lg font-semibold">{formatRate(summary.averageRate)}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Totale interessi</p>
              <p className="font-semibold currency">{formatCurrency(summary.totalInterest)}</p>
            </div>
            <div>
              <p className="text-gray-400">Totale da rimborsare</p>
              <p className="font-semibold currency">{formatCurrency(summary.totalPaid)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
